<script setup lang="ts">
import { computed } from "vue";
import DRBtnIcon from "../UI/DRBtnIcon.vue";
import { useScreen } from "@/composable/useResponsive";
import { useGetMe, useUpdateMyOrganization } from "@/stores/queries";
import { useUpdateTemplateSettings } from "./settings/useUpdateReportSettings";
import DRBtn from "../UI/DRBtn.vue";
import TemplateSettingsForm from "./settings/TemplateSettingsForm.vue";

const props = defineProps<{
  modelValue?: boolean;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const show = computed({
  get: () => !!props.modelValue,
  set: (val: boolean) => {
    emits("update:modelValue", val);
  },
});

const { screen } = useScreen();

const { mutate, isPending } = useUpdateMyOrganization();
const { data: user, isPending: isUserPending } = useGetMe();
const settings = computed(() => {
  return user.value?.organization?.defaultTemplateSettings;
});

const { isChanges, submit } = useUpdateTemplateSettings(
  settings,
  (settings) => {
    mutate({ defaultTemplateSettings: settings });
  }
);
</script>

<template>
  <el-drawer
    v-model="show"
    @close="show = false"
    :show-close="false"
    header-class="border-bottom mb-0 px-0 py-2 "
    :size="screen.gt.sm ? '500px' : '100%'"
  >
    <template #header="{ close }">
      <div class="mx-3">
        <div class="w-100">
          <div
            class="col d-flex flex-row align-items-center justify-content-between gap-3"
          >
            <div class="h4 ps-2">
              {{ $t("dashboard:templates.defaultSettingsDialog.title") }}
            </div>
            <DRBtnIcon
              class="col-auto"
              icon="bi-x-lg"
              variant="flat"
              @click="close"
            />
          </div>

          <div class="px-2">
            <small>
              {{ $t("dashboard:templates.defaultSettingsDialog.description") }}
            </small>
          </div>
        </div>
      </div>
    </template>
    <div v-loading="isUserPending">
      <TemplateSettingsForm
        v-if="modelValue"
        key="default"
        :template-settings="settings"
      />
    </div>
    <template #footer>
      <DRBtn
        class="w-100"
        size="lg"
        icon="bi-save"
        :label="$t('saveModifications')"
        @click="submit"
        :disabled="!isChanges"
        :loading="isPending"
      >
      </DRBtn>
    </template>
  </el-drawer>
</template>

<style lang="scss" scoped>
.styled-tabs {
  .nav-link {
    border: none;
    border-radius: 0;
    background-color: transparent;
    color: var(--el-text-color-secondary);
    &:hover {
      background-color: var(--el-bg-color-light-3);
      color: var(--el-text-color-primary);
    }
    &.active {
      background-color: var(--el-bg-color-light-3);
      color: var(--bs-primary);
      border-bottom: 2px solid var(--bs-primary);
    }
  }
}
</style>
